import React, { useEffect, useState } from "react";
import SummaryApi from "../common";
import { toast } from "react-toastify";

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: "rgb(23, 44, 87)",
  primaryLight: "rgba(23, 44, 87, 0.08)",
  actionRed: "#EF4444",
};

// Format timestamp strings safely (handles "YYYY-MM-DD HH:mm:ss" and skips "0000-00-00")
const formatDateTime = (dateStr) => {
  if (!dateStr || dateStr.startsWith("0000-00-00")) return "Not scheduled";
  const normalized = dateStr.includes("T") ? dateStr : dateStr.replace(" ", "T");
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? "Not scheduled" : d.toLocaleString();
};

const TeamLeadRemainders = () => {
  const [remainders, setRemainders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 6;

  const headers = {
    Authorization: "Bearer sandeep_university_916",
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const fetchRemainders = async () => {
      setLoading(true);
      setError(null);

      const userRaw = sessionStorage.getItem("user");
      if (!userRaw) {
        toast.error("User session not found. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        const user = JSON.parse(userRaw);
        const memberType = user?.member_type;
        const employeeId = user?.employee_id;

        // Select endpoint and request parameters according to member_type
        let apiConfig;
        let requestBody = null;

        if (memberType === "Employee") {
          apiConfig = SummaryApi.getAllFollowUpsByEmployee;
          requestBody = JSON.stringify({ employee_id: employeeId });
        } else if (memberType === "Team Lead") {
          apiConfig = SummaryApi.getAllFollowUpsByTeamLead;
          requestBody = JSON.stringify({ team_lead_emp_id: employeeId });
        } else if (memberType === "Admin" || memberType === "Manager") {
          apiConfig = SummaryApi.getAllFollowUps;
        } else {
          toast.error("Unauthorized user role.");
          setRemainders([]);
          setLoading(false);
          return;
        }

        const response = await fetch(apiConfig.url, {
          method: apiConfig.method,
          headers,
          body:
            requestBody && apiConfig.method.toLowerCase() !== "get"
              ? requestBody
              : undefined,
        });

        const data = await response.json();

        if (data.status && Array.isArray(data.data)) {
          setRemainders(data.data);
        } else {
          setRemainders([]);
          if (data.message && !data.status) {
            setError(data.message);
          }
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch remainders.");
      } finally {
        setLoading(false);
      }
    };

    fetchRemainders();
  }, []);

  // Pagination
  const totalPages = Math.ceil(remainders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = remainders.slice(startIndex, startIndex + itemsPerPage);

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-6 py-4">
          <h2
            className="text-2xl font-bold"
            style={{ color: THEME.primary }}
          >
            All Reminders
          </h2>
        </div>
      </div>

      {/* Content */}
      <div>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div
              className="animate-spin rounded-full h-10 w-10 border-b-2 mb-3"
              style={{ borderColor: THEME.primary }}
            ></div>
            <p className="text-gray-500 font-medium">Loading remainders...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-red-200 shadow-sm px-6 py-10 text-center">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
              style={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}
            >
              <span className="text-red-500 text-2xl font-bold">!</span>
            </div>
            <h3 className="text-lg font-semibold text-red-600">{error}</h3>
            <p className="text-sm text-gray-500 mt-1">
              Please try again later or contact support.
            </p>
          </div>
        ) : remainders.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-6 py-16 text-center">
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
              style={{ backgroundColor: THEME.primaryLight }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-7 h-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                style={{ color: THEME.primary }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2h7m5-5l2 2 4-4"
                />
              </svg>
            </div>
            <h3
              className="text-lg font-semibold"
              style={{ color: THEME.primary }}
            >
              No remainders available
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              You're all caught up. Check back later for new follow-ups.
            </p>
          </div>
        ) : (
          <>
            {/* Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentItems.map((item, index) => {
                // Read description from remarks or callback reasons provided by API
                const description =
                  item.remarks && item.remarks !== "N/A"
                    ? item.remarks
                    : item.call_back_reason && item.call_back_reason !== "N/A"
                      ? item.call_back_reason
                      : item.lead_description || "(No description)";

                return (
                  <div
                    key={item.student_id ? `${item.student_id}-${index}` : index}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                  >
                    {/* Student Name */}
                    <div className="border-b border-gray-100 pb-3 mb-4">
                      <h3
                        className="text-lg font-bold"
                        style={{ color: THEME.primary }}
                      >
                        {item.student_name || "Unknown Student"}
                      </h3>
                    </div>

                    {/* Details */}
                    <div className="space-y-2.5 text-sm text-gray-700">
                      <p>
                        <span
                          className="font-semibold"
                          style={{ color: THEME.primary }}
                        >
                          Next Follow-up:
                        </span>{" "}
                        {formatDateTime(item.next_follow_up_date || item.next_followup_date_time)}
                      </p>
                      <p>
                        <span
                          className="font-semibold"
                          style={{ color: THEME.primary }}
                        >
                          Description:
                        </span>{" "}
                        {description}
                      </p>
                      <p>
                        <span
                          className="font-semibold"
                          style={{ color: THEME.primary }}
                        >
                          Mobile Number:
                        </span>{" "}
                        {item.mobile_number1 || "N/A"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-10 gap-3">
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={currentPage === 1}
                  style={currentPage !== 1 ? { color: THEME.primary } : {}}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition ${currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                    : "bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                >
                  Prev
                </button>

                <span
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
                  style={{ backgroundColor: THEME.primary }}
                >
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  type="button"
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  style={
                    currentPage !== totalPages ? { color: THEME.primary } : {}
                  }
                  className={`px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition ${currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                    : "bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamLeadRemainders;